import { NavigationSection, NavigationSubsection, Topics } from '@app/context/constants';
import { useNavigation } from '@app/hooks/useNavigation';
import { deadlineLabel } from '@components/ui';
import { isUrgentIncident, useIncidentDeadlinesQuery } from '@dieterApi/incident/useIncidentDeadlinesQuery';
import { isUrgentRequest, useRightsRequestsQuery } from '@dieterApi/rightsRequest/useRightsRequestsQuery';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Overview widget: shows up only when data subject requests (Art. 12 Abs. 3 DSGVO) or
// data breaches (Art. 33 Abs. 1 DSGVO, 72h) approach or exceed their statutory deadline.
export function DeadlineTeaser() {
  const { data: rightsData } = useRightsRequestsQuery();
  const { data: incidentData } = useIncidentDeadlinesQuery();
  const { navigateDashboard } = useNavigation();

  const urgentRights = (rightsData?.getRightsRequests || []).filter(isUrgentRequest);
  const urgentIncidents = (incidentData?.getIncidentDeadlines || []).filter(isUrgentIncident);
  const total = urgentRights.length + urgentIncidents.length;
  if (total === 0) return null;

  const hasOverdue = [...urgentRights, ...urgentIncidents].some((item) => item.status === 'OVERDUE');

  const remainingMs = (deadline?: string) =>
    deadline ? new Date(deadline).getTime() - Date.now() : Number.MAX_SAFE_INTEGER;

  const items = [
    ...urgentRights.map((request) => ({
      remaining: remainingMs(request.deadline),
      text: `${request.typeOfRight || 'Anfrage'}: ${deadlineLabel({
        status: request.status,
        deadline: request.deadline,
        isExtended: request.isExtended,
      })}`,
    })),
    ...urgentIncidents.map((incident) => ({
      remaining: remainingMs(incident.deadline),
      text: `Vorfall „${incident.description || 'Unbenannt'}“: ${deadlineLabel({
        status: incident.status,
        deadline: incident.deadline,
        scale: 'hours',
      })}`,
    })),
  ].sort((a, b) => a.remaining - b.remaining);

  const noun =
    urgentIncidents.length === 0
      ? ['Betroffenenanfrage', 'Betroffenenanfragen']
      : urgentRights.length === 0
        ? ['Datenschutzvorfall', 'Datenschutzvorfälle']
        : ['Datenschutz-Vorgang', 'Datenschutz-Vorgänge'];

  return (
    <div className={`rounded-lg p-4 ${hasOverdue ? 'bg-red-50' : 'bg-amber-50'}`} data-testid="deadline-teaser">
      <div className="flex items-center gap-2 mb-1">
        <AccessTimeIcon fontSize="small" className={hasOverdue ? 'text-red-600' : 'text-amber-600'} />
        <span className="font-semibold">
          {total === 1 ? `Ein ${noun[0]} mit ablaufender Frist` : `${total} ${noun[1]} mit ablaufender Frist`}
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-2">{items[0].text}</p>
      <div className="flex gap-4">
        {urgentRights.length > 0 && (
          <button
            type="button"
            className="text-sm font-medium text-primary-root hover:underline"
            onClick={() => navigateDashboard(NavigationSection.Topic, Topics.RIGHTS, NavigationSubsection.Requests)}
          >
            Zu den Anfragen →
          </button>
        )}
        {urgentIncidents.length > 0 && (
          <button
            type="button"
            className="text-sm font-medium text-primary-root hover:underline"
            onClick={() => navigateDashboard(NavigationSection.Topic, Topics.DSV, NavigationSubsection.Incidents)}
          >
            Zu den Vorfällen →
          </button>
        )}
      </div>
    </div>
  );
}
